import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "@/store/user";

/**
 * Mock Moyasar checkout page. Used when PAYMENTS_MODE=mock.
 * Renders a fake card form and posts the outcome back via mockConfirm.
 * In LIVE mode this file is unused (PricingPage will host the real Moyasar form).
 */
import { api } from "@/lib/api";

export default function CheckoutMockPage() {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const { refreshSubscription } = useUser();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const pay = async (outcome) => {
    setErr("");
    setBusy(true);
    try {
      await api.mockConfirm(paymentId, outcome);
      await refreshSubscription();
      if (outcome === "paid") navigate("/checkout/success");
      else navigate("/checkout/failure");
    } catch (e) {
      setErr(
        e?.response?.data?.detail || "تعذّر تأكيد الدفع التجريبيّ. حاول مجدّدًا."
      );
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!paymentId) navigate("/pricing");
  }, [paymentId, navigate]);

  return (
    <div className="shell" data-testid="checkout-mock-page">
      <div className="back-link" onClick={() => navigate("/pricing")}>
        ← العودة إلى الخطط
      </div>

      <div
        className="panel"
        style={{
          maxWidth: 480,
          margin: "30px auto",
          padding: 28,
          border: "1px solid #c79c6a55",
        }}
      >
        <div
          style={{
            background: "#fff1d6",
            color: "#7a5a18",
            padding: "8px 12px",
            borderRadius: 8,
            fontSize: 12,
            textAlign: "center",
            marginBottom: 18,
          }}
        >
          🧪 وضع الاختبار (Mock) — لن يتمّ خصم أيّ مبلغ حقيقيّ.
        </div>

        <h2 style={{ marginBottom: 6, fontSize: 22 }}>إتمام الدفع</h2>
        <p className="muted" style={{ marginBottom: 18, fontSize: 14 }}>
          هذه واجهة محاكاة لبوابة Moyasar السعوديّة. اضغط «دفع ناجح» لتفعيل
          الاشتراك، أو «دفع فاشل» لاختبار التدفّق البديل.
        </p>

        {/* Fake card form (cosmetic) */}
        <div style={{ display: "grid", gap: 12, marginBottom: 18 }}>
          <label style={{ fontSize: 13 }}>
            رقم البطاقة
            <input
              data-testid="mock-card-number"
              type="text"
              readOnly
              value="4111 1111 1111 1111"
              style={inputStyle}
            />
          </label>
          <div style={{ display: "flex", gap: 12 }}>
            <label style={{ flex: 1, fontSize: 13 }}>
              MM/YY
              <input
                readOnly
                value="12/30"
                style={inputStyle}
                data-testid="mock-card-exp"
              />
            </label>
            <label style={{ flex: 1, fontSize: 13 }}>
              CVC
              <input
                readOnly
                value="123"
                style={inputStyle}
                data-testid="mock-card-cvc"
              />
            </label>
          </div>
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "center",
              opacity: 0.85,
              fontSize: 12,
              marginTop: 6,
            }}
          >
            <span>💳 mada</span>
            <span>·</span>
            <span> Apple Pay</span>
            <span>·</span>
            <span>Visa / Mastercard</span>
          </div>
        </div>

        {err && (
          <div
            data-testid="mock-checkout-err"
            style={{ color: "#ff8b8b", marginBottom: 12, fontSize: 14 }}
          >
            {err}
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button
            className="btn btn-primary"
            data-testid="mock-pay-success"
            disabled={busy}
            onClick={() => pay("paid")}
            style={{
              flex: 1,
              background: "#2ec48e",
              color: "#0e1430",
              fontWeight: 700,
              padding: "12px",
            }}
          >
            {busy ? "..." : "✅ دفع ناجح"}
          </button>
          <button
            className="btn btn-ghost"
            data-testid="mock-pay-fail"
            disabled={busy}
            onClick={() => pay("failed")}
            style={{
              flex: 1,
              fontWeight: 600,
              padding: "12px",
            }}
          >
            ❌ دفع فاشل
          </button>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  marginTop: 6,
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #2a2640",
  background: "#13102a",
  color: "#e9e6ff",
  fontSize: 15,
  fontFamily: "monospace",
};
